import { useEffect } from 'react';

/**
 * SimulaBankSEO — Componente de SEO completo
 * Injeta meta tags, Open Graph, Twitter Cards, Schema.org e hreflang.
 * Coloque <SimulaBankSEO /> como primeiro filho de <LandingPage />.
 *
 * Para uso em produção com React Router, considere usar react-helmet-async:
 *   npm install react-helmet-async
 * e substituir useEffect por <Helmet> do pacote.
 */

const SEO_CONFIG = {
  /* ── Dados primários ── */
  siteName:    'SimulaBank',
  siteUrl:     'https://simulabank.com.br',   // ← altere para seu domínio real
  locale:      'pt_BR',
  lang:        'pt-BR',

  /* ── Página principal ── */
  title:       'SimulaBank — Compare Financiamento Imobiliário em 6 Bancos em Segundos',
  titleShort:  'SimulaBank',
  description: 'Simule e compare financiamento imobiliário no BRB, Santander, Itaú, Inter, Bradesco e Caixa em uma única plataforma. Cálculos SAC precisos com MIP, DFI e CET. Ideal para correspondentes bancários e imobiliárias.',
  keywords:    [
    'simulador de financiamento imobiliário',
    'comparar financiamento bancário',
    'simulação crédito imobiliário',
    'correspondente bancário',
    'SAC amortização',
    'financiamento Caixa Econômica',
    'financiamento Itaú Santander Bradesco',
    'CET custo efetivo total',
    'MIP DFI seguro imobiliário',
    'assessoria de crédito imobiliário',
    'simulador multibanco',
    'SimulaBank',
  ].join(', '),

  /* ── Open Graph / redes sociais ── */
  ogImage:     'https://simulabank.com.br/og-image.png', // 1200×630 px
  ogImageAlt:  'SimulaBank — Comparativo de financiamento imobiliário entre 6 bancos',
  twitterHandle: '@simulabank',

  /* ── Schema.org / Rich Snippets ── */
  priceRange:  'R$197–R$597/mês',
  contactPhone: '+55-61-99999-9999',   // ← atualize
  contactEmail: 'contato@simulabank.com.br', // ← atualize
  addressCity: 'Brasília',
  addressState: 'DF',
  addressCountry: 'BR',
};

/* ─────────────────────────────── Schema.org ──────────────────────────────── */

const schemaOrg = {
  '@context': 'https://schema.org',
  '@graph': [
    /* SoftwareApplication */
    {
      '@type': 'SoftwareApplication',
      name: SEO_CONFIG.siteName,
      url: SEO_CONFIG.siteUrl,
      description: SEO_CONFIG.description,
      applicationCategory: 'FinanceApplication',
      operatingSystem: 'Web',
      offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'BRL',
        lowPrice: '197',
        highPrice: '597',
        offerCount: '3',
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '47',
      },
    },
    /* Organization */
    {
      '@type': 'Organization',
      name: SEO_CONFIG.siteName,
      url: SEO_CONFIG.siteUrl,
      logo: `${SEO_CONFIG.siteUrl}/logo.png`,
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: SEO_CONFIG.contactPhone,
        contactType: 'customer support',
        availableLanguage: 'Portuguese',
      },
      address: {
        '@type': 'PostalAddress',
        addressLocality: SEO_CONFIG.addressCity,
        addressRegion: SEO_CONFIG.addressState,
        addressCountry: SEO_CONFIG.addressCountry,
      },
      sameAs: [
        'https://www.linkedin.com/company/simulabank',
        'https://www.instagram.com/simulabank',
        `https://twitter.com/${SEO_CONFIG.twitterHandle.replace('@', '')}`,
      ],
    },
    /* FAQPage — impacta rich snippet no Google */
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'O SimulaBank usa os dados reais dos bancos?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Sim. Os cálculos são calibrados com as tabelas oficiais de cada banco — BRB, Santander, Itaú, Inter, Bradesco e Caixa — incluindo taxas de juros, seguros MIP/DFI e tarifas atualizadas.',
          },
        },
        {
          '@type': 'Question',
          name: 'Qual a diferença entre SAC e Price no financiamento imobiliário?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'No SAC (Sistema de Amortização Constante) a amortização é fixa e os juros diminuem ao longo do tempo, resultando em parcelas decrescentes. O SimulaBank calcula o sistema SAC com precisão para todos os bancos.',
          },
        },
        {
          '@type': 'Question',
          name: 'Posso exportar a simulação em PDF para o cliente?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Sim. O SimulaBank gera relatórios comparativos em PDF com white-label — você pode incluir sua marca e logo antes de enviar ao cliente.',
          },
        },
        {
          '@type': 'Question',
          name: 'O SimulaBank integra com CRM?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Sim. Os planos superiores oferecem integração com Bitrix24, HubSpot e Pipedrive, vinculando simulações diretamente aos negócios no seu CRM.',
          },
        },
        {
          '@type': 'Question',
          name: 'Existe período de teste grátis?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Sim. Você pode testar o SimulaBank por 7 dias sem precisar de cartão de crédito e sem compromisso.',
          },
        },
      ],
    },
    /* BreadcrumbList */
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SEO_CONFIG.siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Funcionalidades', item: `${SEO_CONFIG.siteUrl}/#features` },
        { '@type': 'ListItem', position: 3, name: 'Preços', item: `${SEO_CONFIG.siteUrl}/#precos` },
      ],
    },
  ],
};

/* ─────────────────────────────── Component ───────────────────────────────── */

export default function SimulaBankSEO() {
  useEffect(() => {
    const cfg = SEO_CONFIG;

    /* helpers */
    const setMeta = (attr, val, content) => {
      let el = document.querySelector(`meta[${attr}="${val}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, val); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    const setLink = (rel, href, extra = {}) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement('link'); el.setAttribute('rel', rel); document.head.appendChild(el); }
      el.setAttribute('href', href);
      Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
    };
    const injectJSON = (id, obj) => {
      let el = document.getElementById(id);
      if (!el) { el = document.createElement('script'); el.id = id; el.type = 'application/ld+json'; document.head.appendChild(el); }
      el.textContent = JSON.stringify(obj, null, 2);
    };

    /* ── Title & basic meta ── */
    document.title = cfg.title;
    document.documentElement.lang = cfg.lang;
    setMeta('name', 'description', cfg.description);
    setMeta('name', 'keywords', cfg.keywords);
    setMeta('name', 'robots', 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');
    setMeta('name', 'author', cfg.siteName);
    setMeta('name', 'theme-color', '#0A0F1E');

    /* ── Canonical ── */
    setLink('canonical', cfg.siteUrl);

    /* ── Open Graph ── */
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:url', cfg.siteUrl);
    setMeta('property', 'og:title', cfg.title);
    setMeta('property', 'og:description', cfg.description);
    setMeta('property', 'og:image', cfg.ogImage);
    setMeta('property', 'og:image:alt', cfg.ogImageAlt);
    setMeta('property', 'og:image:width', '1200');
    setMeta('property', 'og:image:height', '630');
    setMeta('property', 'og:site_name', cfg.siteName);
    setMeta('property', 'og:locale', cfg.locale);

    /* ── Twitter Card ── */
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:site', cfg.twitterHandle);
    setMeta('name', 'twitter:creator', cfg.twitterHandle);
    setMeta('name', 'twitter:title', cfg.title);
    setMeta('name', 'twitter:description', cfg.description);
    setMeta('name', 'twitter:image', cfg.ogImage);
    setMeta('name', 'twitter:image:alt', cfg.ogImageAlt);

    /* ── Schema.org JSON-LD ── */
    injectJSON('schema-simulabank', schemaOrg);

    /* ── Google Analytics GA4 ── */
    if (!document.getElementById('gtag-js')) {
      const s1 = document.createElement('script');
      s1.id    = 'gtag-js';
      s1.async = true;
      s1.src   = 'https://www.googletagmanager.com/gtag/js?id=G-W77FQ534V4';
      document.head.appendChild(s1);

      const s2 = document.createElement('script');
      s2.id        = 'gtag-init';
      s2.innerHTML = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','G-W77FQ534V4',{send_page_view:true});`;
      document.head.appendChild(s2);
    }

    /* ── Preconnect / performance ── */
    ['https://fonts.googleapis.com', 'https://fonts.gstatic.com'].forEach((u) =>
      setLink('preconnect', u, u.includes('gstatic') ? { crossorigin: '' } : {})
    );
  }, []);

  return null; // componente invisível
}